import React, { useContext, useEffect, useState } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../auth";
import { TripCtx } from "./TripLayout";
import { toast } from "sonner";
import { BookOpen, ArrowLeft } from "lucide-react";

const cSym = (c) => ({ EUR: "€", GBP: "£" }[c] || c);

export default function MemoryBookOrderPage() {
  const { tripId } = useParams();
  const { API, authHeaders } = useAuth();
  const { trip } = useContext(TripCtx);
  const nav = useNavigate();
  const [quote, setQuote] = useState(null);
  const [tier, setTier] = useState(null);
  const [addr, setAddr] = useState({ full_name: "", line1: "", line2: "", city: "", postcode: "", country: "GB", phone: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/trips/${tripId}/memory-book/quote`, { headers: authHeaders }).then((r) => {
      setQuote(r.data);
      setTier(r.data.recommended_tier);
    });
  }, [tripId]);

  const set = (k) => (e) => setAddr({ ...addr, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!tier) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/memory-book/checkout`,
        { trip_id: tripId, tier, address: addr, origin_url: window.location.origin },
        { headers: authHeaders });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Order failed");
      setBusy(false);
    }
  };

  if (!quote) return <p className="py-6 tcm-eyebrow">Loading...</p>;

  const selected = quote.tiers.find((t) => t.tier === tier);

  return (
    <div data-testid="memory-book-order" className="py-4">
      <button onClick={() => nav(-1)} className="tcm-eyebrow underline decoration-[#C9A961] underline-offset-4 inline-flex items-center gap-1">
        <ArrowLeft size={12} /> Back to Diary
      </button>
      <div className="mt-4 flex items-center gap-3">
        <BookOpen size={22} strokeWidth={1.25} />
        <h1 className="font-display text-4xl">Order printed book</h1>
      </div>
      <hr className="tcm-gold-rule w-16 my-6" />
      <p className="text-sm text-[color:var(--tcm-navy-70)] mb-6">
        Your {quote.entry_count} diary {quote.entry_count === 1 ? "entry becomes" : "entries become"} a keepsake book. Pick a size, add your shipping details, and our team will dispatch it once printed.
      </p>

      <section className="mb-8">
        <p className="tcm-eyebrow mb-3">Choose a size</p>
        <div className="space-y-3">
          {quote.tiers.map((t) => (
            <label key={t.tier} data-testid={`tier-${t.tier}`}
                   className={`block cursor-pointer tcm-double-frame ${tier === t.tier ? "" : "opacity-70"}`}>
              <div className="tcm-inner p-4 flex justify-between items-center" style={tier === t.tier ? { background: "var(--tcm-navy)", color: "var(--tcm-cream)" } : {}}>
                <div>
                  <p className="tcm-eyebrow" style={tier === t.tier ? { color: "var(--tcm-gold)" } : {}}>
                    {t.tier}{quote.recommended_tier === t.tier ? " · recommended" : ""}
                  </p>
                  <p className="font-display text-xl mt-1">{t.label}</p>
                </div>
                <div className="text-right">
                  <p className="font-display text-2xl">{cSym(quote.currency)}{(t.price_minor / 100).toFixed(0)}</p>
                  <input type="radio" name="tier" className="sr-only" checked={tier === t.tier} onChange={() => setTier(t.tier)} />
                </div>
              </div>
            </label>
          ))}
        </div>
      </section>

      <form onSubmit={submit} className="tcm-double-frame">
        <div className="tcm-inner p-5 space-y-4">
          <p className="tcm-eyebrow">Shipping address</p>
          <div><label className="tcm-eyebrow block mb-1">Full name</label>
            <input data-testid="addr-name" required className="tcm-input" value={addr.full_name} onChange={set("full_name")} /></div>
          <div><label className="tcm-eyebrow block mb-1">Address line 1</label>
            <input data-testid="addr-line1" required className="tcm-input" value={addr.line1} onChange={set("line1")} /></div>
          <div><label className="tcm-eyebrow block mb-1">Address line 2 (optional)</label>
            <input data-testid="addr-line2" className="tcm-input" value={addr.line2} onChange={set("line2")} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="tcm-eyebrow block mb-1">City</label>
              <input data-testid="addr-city" required className="tcm-input" value={addr.city} onChange={set("city")} /></div>
            <div><label className="tcm-eyebrow block mb-1">Post code</label>
              <input data-testid="addr-postcode" required className="tcm-input" value={addr.postcode} onChange={set("postcode")} /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="tcm-eyebrow block mb-1">Country</label>
              <input data-testid="addr-country" required className="tcm-input" value={addr.country} onChange={set("country")} /></div>
            <div><label className="tcm-eyebrow block mb-1">Phone</label>
              <input data-testid="addr-phone" className="tcm-input" value={addr.phone} onChange={set("phone")} /></div>
          </div>
          <hr className="tcm-gold-rule mt-2" />
          <div className="flex justify-between items-center">
            <p className="tcm-eyebrow">Total</p>
            <p className="font-display text-3xl">{cSym(quote.currency)}{selected ? (selected.price_minor / 100).toFixed(0) : "—"}</p>
          </div>
          <p className="text-[10px] tcm-eyebrow text-[color:var(--tcm-navy-70)]">A card processing fee is added at checkout.</p>
          <button data-testid="book-checkout" disabled={busy || !tier || quote.entry_count === 0}
                  className="tcm-btn-primary w-full">
            {quote.entry_count === 0 ? "Add a diary entry first" : busy ? "Redirecting..." : "Continue to payment"}
          </button>
        </div>
      </form>
    </div>
  );
}
